import { Box, Container, Typography, useTheme } from "@mui/material";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import io from "socket.io-client";
import { appIcons, appImages } from "../../assets";
import GlobalButton from "../../components/button";
import CompanyDetailCard from "../../components/cards/CompanyDetailCard";
import ChatDialogWithWorker from "../../components/dialog/ChatDialog";
import AppFooter from "../../components/footer";
import LoginDialog from "../../components/loginDialog";
import NavBar from "../../components/NavBar";
import WorkerDetailSkeleton from "../../components/skeletons/workerDetailSkeleton";
import { useSnackbar } from "../../lib/context/snackbarContext";
import { socketUrl } from "../../services";
import { createChatWithWorker } from "../../services/chatFn";
import { getCompanyServiceDetail } from "../../services/companyService";
import { getWorkerDetail } from "../../services/workerFn";

const socket = io.connect(socketUrl);

const CompanyDetail = () => {
  const { id } = useParams();
  const { palette } = useTheme();
  const navigate = useNavigate();
  const openLoginDialog = useRef(null);
  const openChatDialog = useRef(null);
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const subserviceId = searchParams.get("service");
  const type = searchParams.get("type");

  const [chatLoading, setChatLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [workerDetail, setWorkerDetail] = useState(null);
  const [serviceDetail, setServiceDetail] = useState(null);

  const userData = useSelector((state) => state.userReducer.user);

  console.log(type, subserviceId, "searchParamssearchParamssearchParams");

  useEffect(() => {
    if (id && subserviceId) {
      fetchDetails(id, subserviceId);
    }
  }, [id, subserviceId]);

  const fetchDetails = async (workerId, subserviceId) => {
    try {
      setIsLoading(true);

      // Fetch worker details and service details sequentially
      const workerResponse = await getWorkerDetail(workerId);
      if (workerResponse?.data.status !== "success") {
        showSnackbar(workerResponse.data.message, "error");
        return;
      }

      const workerData = workerResponse.data.data;
      setWorkerDetail(workerData);

      // Fetch service details using companyId and subserviceId
      const serviceResponse = await getCompanyServiceDetail(
        workerData.companyId._id,
        subserviceId
      );
      if (serviceResponse?.data.status !== "success") {
        showSnackbar(serviceResponse.data.message, "error");
        return;
      }

      setServiceDetail(serviceResponse.data.data);
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookNow = () => {
    if (!userData) {
      openLoginDialog.current.openDialog();
    } else {
      if (!userData?.createdBy)
        navigate(`/book-appointment/${id}/${subserviceId}`);
      else if (
        userData?.createdBy &&
        userData?.createdBy == workerDetail?.companyId?._id
      )
        navigate(`/book-appointment/${id}/${subserviceId}`);
      else
        showSnackbar(
          "You cannot book this worker, because this worker is not associated with your company",
          "error"
        );
    }
  };

  const handleChatWithWorker = async () => {
    if (!userData) {
      return openLoginDialog.current.openDialog();
    }

    try {
      let payload = {
        workerId: id,
        userId: userData._id,
      };

      setChatLoading(true);
      const response = await createChatWithWorker(payload);
      if (response.data.status === "success") {
        const data = response.data.data;
        openChatDialog.current.openDialog({ ...data, companyId: id });
      } else {
        showSnackbar(response.data.message);
      }
    } catch (error) {
      showSnackbar(error.message);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <>
      {/* Hero Section */}
      <NavBar />
      <Box
        height={{ xs: "60vh", sm: "100vh" }}
        display="flex"
        flexDirection="column"
        position={"relative"}
        marginTop={7}
        sx={{
          background: palette.gradient.background,
          backgroundImage: `url(${
            type == "cleaning"
              ? appImages.cleaningBgImg
              : appImages.carServiceBgImg
          })`,
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* <Header /> */}

        <img
          src={appImages.helpPageWaveImg}
          width={"100%"}
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            left: 0,
            zIndex: 1,
          }}
        />
      </Box>

      {/* Content Section */}
      <Box position={"absolute"} top={{ xs: 130, sm: 114 }} left={0} right={0}>
        <Container
          sx={{
            maxWidth: "1100px !important",
            position: "relative",
            zIndex: 999,
            flexGrow: 1,
          }}
        >
          <GlobalButton
            handleClickBtn={() => navigate(-1)}
            variant={"outlined"}
            btnText={"goBack"}
            startIcon={<img src={appIcons.whiteArrowBackIcon} width={30} />}
            styleProps={{
              color: "text.white",
              borderColor: "text.white",
            }}
          />
          <Typography
            variant="heading_pr"
            component={"h1"}
            maxWidth={700}
            margin={"auto"}
            textAlign={"center"}
            color={"text.white"}
            lineHeight={"normal"}
            mb={5}
          >
            {serviceDetail?.mainServiceId?.name.en === "cleaning"
              ? `${t("workerDetailHead")}.`
              : `
            ${t("workerDetailHeadVehicle")}.`}
          </Typography>

          {isLoading ? (
            <WorkerDetailSkeleton />
          ) : (
            <CompanyDetailCard
              detail={workerDetail?.companyId}
              workerDetail={workerDetail}
              serviceDetail={serviceDetail}
              handleBookNow={handleBookNow}
              handleChatNow={handleChatWithWorker}
              chatLoading={chatLoading}
            />
          )}
        </Container>
        <AppFooter bgcolor={true} />
      </Box>
      <LoginDialog ref={openLoginDialog} />
      <ChatDialogWithWorker ref={openChatDialog} socket={socket} />
    </>
  );
};

export default CompanyDetail;
